import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import adminApi from './api'
import { Notification } from './components/Notification'
import { ConfirmModal } from './components/ConfirmModal'

interface Planting {
  id: number
  name: { ru?: string; ky?: string; en?: string }
  locationName: string | null
  latitude: number | null
  longitude: number | null
  treesPlanted: number
  plantedAt: string
  eventId: number | null
  regionId: number | null
  regionName: { ru?: string; ky?: string; en?: string } | null
  regionCode: string | null
}

export const PlantingsAdmin: React.FC = () => {
  const [plantings, setPlantings] = useState<Planting[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<{ show: boolean; id: number | null }>({ show: false, id: null })
  const [userRole, setUserRole] = useState('')
  const [openMenuId, setOpenMenuId] = useState<number | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const userStr = localStorage.getItem('admin_user')
    if (userStr) {
      try {
        setUserRole(JSON.parse(userStr).role || '')
      } catch {}
    }
  }, [])

  useEffect(() => {
    fetchPlantings()
  }, [])

  const fetchPlantings = async () => {
    try {
      const { data } = await adminApi.get('/api/admin/plantings')
      setPlantings(data.plantings || [])
      setError('')
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Ошибка загрузки посадок')
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (confirmDelete.id == null) return
    try {
      await adminApi.delete(`/api/admin/plantings/${confirmDelete.id}`)
      setNotification({ message: 'Посадка удалена', type: 'success' })
      fetchPlantings()
    } catch (err: any) {
      setNotification({ message: err?.response?.data?.message || 'Ошибка удаления', type: 'error' })
    } finally {
      setConfirmDelete({ show: false, id: null })
    }
  }

  const displayName = (p: Planting) => p.name?.ru || p.name?.ky || p.name?.en || p.locationName || '—'

  return (
    <div>
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="page-header flex items-center justify-between gap-4">
        <div>
          <h1 className="page-title">Посадки</h1>
          <p className="page-subtitle">Точки посадок деревьев на карте Кыргызстана</p>
        </div>
        <button onClick={() => navigate('/admin/plantings/new')} className="btn-primary">
          Добавить посадку
        </button>
      </div>

      {error && <div className="alert alert-error mb-5">{error}</div>}

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="loading-spinner w-10 h-10"></div>
        </div>
      ) : (
        <div className="table-card">
          <table className="admin-table">
            <thead>
              <tr>
                <th className="w-[28%]">Название</th>
                <th className="w-[20%]">Локация</th>
                <th className="w-[120px]">Область</th>
                <th className="w-[120px]">Дата</th>
                <th className="w-[110px]">Деревьев</th>
                <th className="w-[160px]">Координаты</th>
                <th className="w-[50px]"></th>
              </tr>
            </thead>
            <tbody>
              {plantings.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className="empty-state">
                      <div className="empty-state-text">Посадок пока нет</div>
                    </div>
                  </td>
                </tr>
              ) : (
                plantings.map(p => (
                  <tr key={p.id} className="cursor-pointer" onClick={() => navigate(`/admin/plantings/${p.id}/edit`)}>
                    <td>
                      <div className="font-medium text-[#1890ff]">{displayName(p)}</div>
                    </td>
                    <td>{p.locationName || '—'}</td>
                    <td className="text-sm">
                      {p.regionName?.ru || p.regionCode || <span className="text-gray-400">—</span>}
                    </td>
                    <td>{new Date(p.plantedAt).toLocaleDateString('ru-RU')}</td>
                    <td>{p.treesPlanted.toLocaleString('ru-RU')}</td>
                    <td className="text-xs text-gray-500">
                      {p.latitude?.toFixed(5)}, {p.longitude?.toFixed(5)}
                    </td>
                    <td style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
                      <button
                        onClick={e => {
                          e.stopPropagation()
                          setOpenMenuId(openMenuId === p.id ? null : p.id)
                        }}
                        className="bg-transparent border-0 cursor-pointer px-2 py-1 text-lg text-gray-500 leading-none"
                        aria-label="Действия"
                      >
                        ⋮
                      </button>
                      {openMenuId === p.id && (
                        <>
                          <div
                            className="fixed inset-0 z-[999]"
                            onClick={e => {
                              e.stopPropagation()
                              setOpenMenuId(null)
                            }}
                          />
                          <div
                            onClick={e => e.stopPropagation()}
                            className="absolute right-2 top-8 bg-white border border-[#e0e0e0] rounded shadow-md z-[1000] min-w-[150px]"
                          >
                            <button
                              onClick={() => {
                                setOpenMenuId(null)
                                navigate(`/admin/plantings/${p.id}/edit`)
                              }}
                              className="block w-full px-4 py-2.5 border-0 bg-transparent text-left cursor-pointer text-sm text-[#333] hover:bg-[#f5f5f5]"
                            >
                              Изменить
                            </button>
                            {userRole === 'admin' && (
                              <button
                                onClick={() => {
                                  setOpenMenuId(null)
                                  setConfirmDelete({ show: true, id: p.id })
                                }}
                                className="block w-full px-4 py-2.5 border-0 bg-transparent text-left cursor-pointer text-sm text-[#ff4d4f] hover:bg-[#fff2f0]"
                              >
                                Удалить
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmDelete.show}
        title="Удалить посадку?"
        message="Это действие нельзя отменить."
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete({ show: false, id: null })}
        confirmText="Удалить"
      />
    </div>
  )
}
